import { AddAPhotoOutlined, DeleteRounded, NoPhotographyOutlined } from '@mui/icons-material';
import { Box, IconButton, ThemeProvider, useTheme } from '@mui/material';
import { FormControlDefault } from 'app/shared/layout/register/DefaultFormsComponents';
import { showElement } from 'app/shared/util/data-utils';
import React, { useState } from 'react';
import ImageModal from './ImageModal';

interface IInspectionImageInputProps {
  image: string;
  description?: string;
  imageContentType: string;
  handleInputChange: (name: string, value: any, index: number) => void;
  index: number;
  disabled?: boolean;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const InspectionImageInput: React.FC<IInspectionImageInputProps> = ({ image, description, imageContentType, handleInputChange, index, disabled }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleFileInputChange = async (event: any) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      try {
        const base64String = await fileToBase64(file);
        handleInputChange('image', base64String, index);
      } catch (error) {
        console.error('Error converting file to base64:', error);
      }
    }
  };

  const cleanImage = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleInputChange('image', null, index);
  };

  return (
    <ThemeProvider theme={theme}>
      <FormControlDefault sx={{ width: '110px', height: '80px', margin: '0 auto' }}>
        <Box position="relative" width="100%" height="100%">
          <Box
            sx={{
              border: '1px dashed',
              borderColor: disabled ? theme.palette.divider : 'gray',
              borderRadius: '8px',
              width: '100%',
              height: '100%',
              cursor: disabled ? 'not-allowed' : 'pointer',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <input
              id="icon-button-file"
              type="file"
              style={{
                width: '100%',
                height: '100%',
                opacity: 0,
                position: 'absolute',
                inset: 0,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              disabled={disabled}
              onChange={event => handleFileInputChange(event)}
            />
            {image ? (
              <>
                <Box component="a" width="100%" height="100%" onClick={() => setIsOpen(true)} sx={{ position: 'absolute', top: 0, left: 0 }}>
                  <img
                    src={`data:${imageContentType};base64,${image}`}
                    alt="imagem-ativo"
                    width="100%"
                    height="100%"
                    style={{ borderRadius: '8px', objectFit: 'cover' }}
                  />
                </Box>
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    background: theme.palette.background.paper,
                    color: theme.palette.error.main,
                    ':hover': { backgroundColor: theme.palette.background.paper },
                    padding: '6px',
                  }}
                  style={showElement(!disabled)}
                  onClick={cleanImage}
                >
                  <DeleteRounded fontSize="small" />
                </IconButton>
              </>
            ) : (
              <Box component="label" htmlFor="icon-button-file">
                {disabled ? (
                  <NoPhotographyOutlined fontSize="medium" sx={{ color: theme.palette.grey[400], ':hover': { background: 'none' }, mt: 1 }} />
                ) : (
                  <AddAPhotoOutlined fontSize="medium" sx={{ color: theme.palette.secondary.main, ':hover': { background: 'none' }, mt: 1 }} />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </FormControlDefault>
      {open && <ImageModal imageUrl={`data:${imageContentType};base64,${image}`} description={description} isOpen={isOpen} setIsOpen={setIsOpen} />}
    </ThemeProvider>
  );
};
